/* The shell's one touch gesture: pull to refresh. Nothing else in the app
   claims a gesture, and this is the only place that reads raw touch events.

   Framework-free and DOM-only, like the rest of core/ — the shell passes
   callbacks in, so the recognition can be reasoned about (and exercised) without
   Lit.

   A horizontal swipe-between-workspaces gesture was built alongside this one
   (see ADR 0010, decisions 1 and 3) and then reverted — it lagged noticeably on
   the reference device and the win over tapping a tab was marginal. The
   inset-vs-edge machinery it needed is gone with it; if it comes back, read ADR
   0010's "Reverted" section first. `claimed()` below is the one piece that came
   back, for the vertical axis.

   Note the listeners must be non-passive explicitly: Chrome defaults
   touchstart/touchmove to passive on window and document, and a passive listener
   silently ignores preventDefault. */

export interface Sample {
  /** Total movement from the touch origin, px. */
  dy: number;
  /** Instantaneous velocity over the last few moves, px/ms. Sign follows dy. */
  vy: number;
}

export interface ShellGestureHandlers {
  /**
   * A downward drag began while the scroller was already at the top.
   *
   * Return false to decline — the shell does this when the active workspace has
   * not declared itself refreshable.
   */
  onPullStart(): boolean;
  onPullMove(s: Sample): void;
  onPullEnd(s: Sample): void;

  /** Current scroll offset of the shell's scroller. Pull arms only at <= 0. */
  scrollTop(): number;
}

export interface ShellGestureOptions {
  /** Px of travel before an axis is committed. */
  threshold?: number;
}

type Mode = 'idle' | 'watching' | 'pull' | 'declined';

/**
 * Marks a subtree that owns vertical drags itself, so the shell's pull stands
 * down inside it.
 *
 * An overlay that renders INSIDE a workspace is inside the element these
 * listeners are attached to, so without this a sheet dragged down on a
 * refreshable workspace pulls the workspace with it — measured at 21 pull
 * frames and 62% of the commit threshold on one dismissal. Detail avoids this a
 * different way (the shell makes `.body` inert while it is open) and Map avoids
 * it only by not declaring `refreshable` at all.
 */
export const GESTURE_ATTR = 'data-owns-gestures';

/**
 * Whether anything between the touch and the listener claims the gesture.
 *
 * composedPath(), because the claimant is usually inside a shadow root and
 * `target` retargets to the host before it ever reaches here.
 */
function claimed(e: TouchEvent): boolean {
  return e
    .composedPath()
    .some((n) => n instanceof Element && n.hasAttribute(GESTURE_ATTR));
}

/**
 * Attach the shell's pull-to-refresh gesture to an element. Returns a detach
 * function.
 *
 * Only single-finger gestures are recognised: a second touch abandons whatever
 * was in flight, so a pinch is never mistaken for a pull.
 */
export function attachShellGestures(
  el: HTMLElement,
  h: ShellGestureHandlers,
  options: ShellGestureOptions = {},
): () => void {
  const threshold = options.threshold ?? 8;

  let mode: Mode = 'idle';
  let id = -1;
  let x0 = 0;
  let y0 = 0;
  /** Previous sample, for velocity. */
  let py = 0;
  let pt = 0;
  let vy = 0;

  const sample = (): Sample => ({ dy: py - y0, vy });

  const reset = () => {
    mode = 'idle';
    id = -1;
    vy = 0;
  };

  const onStart = (e: TouchEvent) => {
    if (mode !== 'idle') {
      // A second finger during a gesture: abandon rather than fight a pinch.
      if (mode === 'pull') h.onPullEnd(sample());
      mode = 'declined';
      return;
    }
    if (e.touches.length !== 1) { mode = 'declined'; return; }
    // Decided once, at the start: a gesture that begins inside a claimant stays
    // declined even if the finger later leaves it.
    if (claimed(e)) { mode = 'declined'; return; }
    const t = e.touches[0]!;
    id = t.identifier;
    x0 = t.clientX;
    y0 = py = t.clientY;
    pt = e.timeStamp;
    mode = 'watching';
  };

  const onMove = (e: TouchEvent) => {
    if (mode === 'idle' || mode === 'declined') return;
    const t = Array.from(e.touches).find((x) => x.identifier === id);
    if (!t) return;

    const dt = Math.max(1, e.timeStamp - pt);
    vy = (t.clientY - py) / dt;
    py = t.clientY;
    pt = e.timeStamp;

    if (mode === 'watching') {
      const dx = t.clientX - x0;
      const dy = py - y0;
      if (Math.hypot(dx, dy) < threshold) return;

      if (dy > 0 && Math.abs(dy) > Math.abs(dx) && h.scrollTop() <= 0) {
        mode = h.onPullStart() ? 'pull' : 'declined';
      } else {
        // Horizontal, or an upward drag: leave it to the browser and do not
        // reconsider for the rest of this gesture.
        mode = 'declined';
      }
      if (mode === 'declined') return;
    }

    if (mode === 'pull') {
      h.onPullMove(sample());
    }
  };

  const onEnd = (e: TouchEvent) => {
    if (Array.from(e.touches).some((t) => t.identifier === id)) return;
    if (mode === 'pull') h.onPullEnd(sample());
    reset();
  };

  // passive:false — see the note at the top. Removal takes only `capture`, so
  // the two option objects are deliberately not the same shape.
  const add = { passive: false };
  const start = onStart as EventListener;
  const move = onMove as EventListener;
  const end = onEnd as EventListener;
  el.addEventListener('touchstart', start, add);
  el.addEventListener('touchmove', move, add);
  el.addEventListener('touchend', end, add);
  el.addEventListener('touchcancel', end, add);

  return () => {
    el.removeEventListener('touchstart', start);
    el.removeEventListener('touchmove', move);
    el.removeEventListener('touchend', end);
    el.removeEventListener('touchcancel', end);
  };
}
